import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterPanel, SelectFilter } from '@/components/FilterPanel'
import { SearchInput } from '@/components/SearchInput'
import { StatusBadge } from '@/components/StatusBadge'
import { UserAvatar } from '@/components/UserAvatar'
import { useUsers, useOrganizations } from '@/hooks/useRootQueries'
import type { PlatformUser } from '@/types/api'
import { relativeTime, exactTimestamp } from '@/utils/time'

export function Users() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const orgsQuery = useOrganizations({ pageSize: 100 })
  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsQuery.data?.data?.map((o) => ({ value: o.id, label: o.name })) || []),
  ]

  const filters = useMemo(
    () => ({
      search: params.get('search') ?? '',
      organization: params.get('organization') ?? '',
      role: params.get('role') ?? '',
      status: params.get('status') ?? '',
      type: params.get('type') ?? '',
      page: Number(params.get('page') ?? '1'),
      pageSize: 25,
    }),
    [params],
  )

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'page') next.set('page', '1')
    setParams(next)
  }

  const users = useUsers(filters)

  const columns: Column<PlatformUser>[] = [
    {
      key: 'name',
      header: 'User Identity & Email',
      render: (row) => (
        <div className="flex items-center gap-2.5">
          <UserAvatar name={row.name} size={32} />
          <div>
            <div className="text-slate-900 font-bold text-xs">{row.name}</div>
            <div className="font-mono text-[10.5px] text-slate-500 font-semibold select-all">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (row) => (
        <div>
          <div className="font-bold text-slate-800 text-xs truncate max-w-[180px]">
            {row.orgName || 'Organization'}
          </div>
          {row.orgId && (
            <div className="font-mono text-[10px] text-slate-400 truncate max-w-[120px]">
              ID: {row.orgId.slice(-6)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => (
        <span className="capitalize font-semibold text-slate-800 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md text-[11px]">
          {row.role || 'Member'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'documents',
      header: 'Documents',
      render: (row) => {
        const count = row.documentsCount ?? 0
        const completed = row.completedCount ?? 0
        return (
          <div>
            <div className="font-mono font-bold text-slate-900 text-xs">
              {count} {count === 1 ? 'doc' : 'docs'}
            </div>
            {completed > 0 && (
              <div className="font-mono text-[10px] text-emerald-600 font-semibold">
                {completed} completed
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: 'lastActivity',
      header: 'Last Activity / Login',
      render: (row) => {
        const ts = row.lastActivityAt || row.lastLoginAt
        return (
          <div>
            <div className="font-semibold text-slate-900 text-xs">
              {ts ? relativeTime(ts) : 'Never'}
            </div>
            {ts && (
              <div className="font-mono text-[10px] text-slate-400">{exactTimestamp(ts)}</div>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">Platform Users Directory</h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Registered account holders, organization members, and platform administrators.
          </p>
        </div>
      </div>

      <FilterPanel>
        <div className="min-w-[220px] flex-1">
          <span className="mb-1 block text-[10.5px] font-bold text-slate-500 uppercase">Search Users</span>
          <SearchInput
            value={filters.search}
            onChange={(v) => updateParam('search', v)}
            placeholder="Search by name, email, or user ID…"
          />
        </div>
        <SelectFilter
          label="Organization"
          value={filters.organization}
          options={orgOptions}
          onChange={(v) => updateParam('organization', v)}
        />
        <SelectFilter
          label="Status"
          value={filters.status}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'disabled', label: 'Disabled' },
          ]}
          onChange={(v) => updateParam('status', v)}
        />
        {(filters.search || filters.organization || filters.role || filters.status) && (
          <button
            onClick={() => setParams(new URLSearchParams())}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-xs"
          >
            Reset
          </button>
        )}
      </FilterPanel>

      <DataTable
        columns={columns}
        rows={users.data?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={users.isLoading}
        isError={users.isError}
        errorMessage="Could not load users."
        onRetry={() => users.refetch()}
        emptyTitle="No registered users found"
        emptyDescription="Try adjusting your search or organization filters."
        onRowClick={(row) => navigate(`/users/${row.id}`)}
        page={filters.page}
        totalPages={users.data?.meta.totalPages ?? 1}
        totalItems={users.data?.meta.totalItems ?? 0}
        onPageChange={(p) => updateParam('page', String(p))}
      />
    </div>
  )
}
